import { useEffect, useCallback } from 'react';
import WEB_FRAME_CONSTANTS from './webFrame.constants';

const useWebFrame = (isOpen: boolean, onClose: () => void) => {
  const handleSuccessful = useCallback(() => {
    onClose();
  }, [onClose]);

  const addEventListeners = useCallback((successHandler: () => void) => {
    const aipriseFrame = document.querySelector('aiprise-frame');

    if (aipriseFrame) {
      aipriseFrame.removeEventListener('aiprise:successful', successHandler);
      aipriseFrame.addEventListener('aiprise:successful', successHandler);
      return aipriseFrame;
    }

    return null;
  }, []);

  const createRetryTimers = useCallback(
    (retryFunction: () => void, timeouts: number[]) =>
      timeouts.map((timeout) => setTimeout(retryFunction, timeout)),
    []
  );

  const clearTimers = useCallback((timers: NodeJS.Timeout[]) => {
    timers.forEach((timer) => clearTimeout(timer));
  }, []);

  const removeEventListeners = useCallback((successHandler: () => void) => {
    const currentAipriseFrame = document.querySelector('aiprise-frame');

    if (currentAipriseFrame) {
      currentAipriseFrame.removeEventListener(
        'aiprise:successful',
        successHandler
      );
    }

    document.removeEventListener('aiprise:successful', successHandler);
    window.removeEventListener('aiprise:successful', successHandler);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let aipriseElement = addEventListeners(handleSuccessful);

    const retryAddingListeners = () => {
      if (!aipriseElement) {
        aipriseElement = addEventListeners(handleSuccessful);
      }
    };

    const retryTimeouts = [
      WEB_FRAME_CONSTANTS.TIMEOUTS.ONE_HUNDRED_MS,
      WEB_FRAME_CONSTANTS.TIMEOUTS.FIVE_HUNDRED_MS,
      WEB_FRAME_CONSTANTS.TIMEOUTS.ONE_SECOND,
      WEB_FRAME_CONSTANTS.TIMEOUTS.TWO_SECONDS,
    ];

    const timers = createRetryTimers(retryAddingListeners, retryTimeouts);

    return () => {
      clearTimers(timers);
      removeEventListeners(handleSuccessful);
    };
  }, [
    isOpen,
    handleSuccessful,
    addEventListeners,
    createRetryTimers,
    clearTimers,
    removeEventListeners,
  ]);
};

export default useWebFrame;
